import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const CONDS = ["healthy", "hypertension", "diabetes", "gastritis", "other"];

function computeAge(bd) {
  if (!bd) return "";
  try {
    const b = new Date(bd);
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
    return a;
  } catch {
    return "";
  }
}

export default function Onboarding() {
  const { t } = useLang();
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [conditions, setConditions] = useState(["healthy"]);
  const [conditionsOther, setConditionsOther] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [weight, setWeight] = useState(65);
  const [height, setHeight] = useState(170);
  const [allergies, setAllergies] = useState("");
  const [activity, setActivity] = useState("light");
  const [recovery, setRecovery] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleCond = (c) => {
    setConditions((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const submit = async () => {
    if (!birthdate) {
      toast.error(t("birthdate_required"));
      setStep(2);
      return;
    }
    setSaving(true);
    try {
      await api.post("/profile", {
        conditions,
        conditions_other: conditionsOther,
        birthdate,
        weight_kg: Number(weight),
        height_cm: Number(height),
        allergies: allergies.split(",").map((s) => s.trim()).filter(Boolean),
        activity_level: activity,
        recovery_mode: recovery,
        notes,
      });
      await refreshUser();
      toast.success("Profile saved");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="w-full max-w-xl card-soft p-8 md:p-10">
        <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold">
          {t("step")} {step} {t("of")} 3
        </p>
        <h1 className="font-serif-display text-3xl md:text-4xl text-stone-900 mt-2">{t("onboard_title")}</h1>
        <p className="text-stone-500 mt-1">{t("onboard_sub")}</p>

        <div className="mt-8 space-y-6">
          {step === 1 && (
            <div>
              <Label className="text-stone-700">{t("conditions_label")}</Label>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {CONDS.map((c) => (
                  <label key={c} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${conditions.includes(c) ? "border-[#4A5D23] bg-[#EAF0E4]" : "border-stone-200 bg-white"}`} data-testid={`cond-${c}`}>
                    <Checkbox checked={conditions.includes(c)} onCheckedChange={() => toggleCond(c)} />
                    <span className="text-stone-800">{t(c)}</span>
                  </label>
                ))}
              </div>
              <label className="mt-4 flex items-center gap-3">
                <Checkbox checked={recovery} onCheckedChange={(v) => setRecovery(!!v)} data-testid="recovery-mode" />
                <span className="text-stone-700">{t("recovery_mode")}</span>
              </label>
              {conditions.includes("other") && (
                <div className="mt-4">
                  <Label className="text-stone-700">{t("conditions_other_label")}</Label>
                  <Textarea
                    value={conditionsOther}
                    onChange={(e) => setConditionsOther(e.target.value)}
                    rows={2}
                    placeholder={t("conditions_other_placeholder")}
                    className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30"
                    data-testid="conditions-other-input"
                  />
                  <p className="mt-1 text-xs text-stone-500">{t("conditions_other_help")}</p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-stone-700">{t("birthdate")}</Label>
                <Input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)} max={new Date().toISOString().split("T")[0]} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="birthdate-input" />
                {birthdate && <p className="mt-1 text-xs text-stone-500">{t("age_label")}: {computeAge(birthdate)} {t("years_old")}</p>}
              </div>
              <div>
                <Label className="text-stone-700">{t("weight")}</Label>
                <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="weight-input" />
              </div>
              <div>
                <Label className="text-stone-700">{t("height")}</Label>
                <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="height-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-stone-700">{t("activity_level")}</Label>
                <Select value={activity} onValueChange={setActivity}>
                  <SelectTrigger className="mt-1 rounded-xl bg-stone-50 border-transparent" data-testid="activity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["sedentary", "light", "moderate", "active", "very_active"].map((a) => (
                      <SelectItem key={a} value={a}>{t(a)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-stone-700">{t("allergies")}</Label>
                <Input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="peanut, gluten, shellfish" className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="allergies-input" />
              </div>
              <div>
                <Label className="text-stone-700">{t("notes")}</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" rows={4} data-testid="notes-input" />
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="back-btn">
            {t("back")}
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} className="rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white px-8 btn-pop" data-testid="next-btn">
              {t("next")}
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white px-8 btn-pop" data-testid="save-profile-btn">
              {saving ? "…" : t("save_profile")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
